// frontend/src/App.js
import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom"; // React Router for SPA routing
import Navbar from "./components/Navbar"; // Top navigation bar
import Footer from "./components/Footer"; // Page footer
import Home from "./pages/Home"; // Home page component
import Sports from "./pages/Sports"; // Sports news page
import Technology from "./pages/Technology"; // Technology news page
import Business from "./pages/Business"; // Business news page
import Health from "./pages/Health"; // Health news page
import Entertainment from "./pages/Entertainment"; // Entertainment news page
import Politics from "./pages/Politics"; // Politics news page
import Contactus from "./pages/Contactus"; // Contact page
import Advertise from "./pages/Advertise"; // Advertise page
import PrivacyPolicy from "./pages/PrivacyPolicy"; // Privacy policy page
import NotFound from "./components/NotFound"; // 404 page
import ScrollToTopButton from "./components/ScrollToTopButton"; // Scroll-to-top floating button
import { gsap } from "gsap"; // GSAP animation library
import "./index.css"; // Tailwind CSS output


const App = () => {
  const [searchQuery, setSearchQuery] = useState(""); // Holds search query typed in Navbar
  const [pageReady, setPageReady] = useState(false); // Indicates if home data is loaded to show page

  /**
   * Preload Home news with caching logic
   * - Checks if cachedHomeNews exists in localStorage and is fresh (<5 min old)
   * - If cache is valid, pageReady is set immediately
   * - Otherwise, fetch latest data from backend, store in localStorage, and set pageReady
   */
  useEffect(() => {
    const cachedNews = localStorage.getItem("cachedHomeNews");
    const cacheTime = localStorage.getItem("cachedHomeNewsTime");

    if (cachedNews && cacheTime && Date.now() - parseInt(cacheTime) < 1000 * 60 * 5) {
      // Cache exists and is fresh (<5 minutes old)
      setPageReady(true);
    } else {
      // Fetch latest home news from backend
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/news/home`)
        .then((res) => res.json())
        .then((data) => {
          // Save fetched news and timestamp to localStorage
          localStorage.setItem("cachedHomeNews", JSON.stringify(data));
          localStorage.setItem("cachedHomeNewsTime", Date.now().toString());
        })
        .catch((err) => console.error(err))
        .finally(() => setPageReady(true)); // Page is ready after fetch
    }
  }, []);

  /**
   * GSAP fade-in animation for elements with class 'fade-in'
   * - Only runs after page is ready
   * - Applies staggered entrance animation for smooth UX
   */
  useEffect(() => {
    if (pageReady) {
      gsap.from(".fade-in", {
        duration: 1,
        opacity: 0,
        y: 30,
        stagger: 0.2,
        ease: "power2.out",
      });
    }
  }, [pageReady]);

  // Loading screen while home news is being fetched or cached
  if (!pageReady) {
    return (
      <div className="flex justify-center items-center min-h-screen w-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  return (
    // Main app container
    <div className="bg-gray-100 text-gray-800 min-h-screen flex flex-col">
      {/* Navbar with search functionality */}
      <Navbar onSearch={setSearchQuery} />

      {/* Main content area */}
      <main className="flex-1 fade-in">
        <Routes>
          {/* Routes for all pages, passing searchQuery to news pages */}
          <Route path="/" element={<Home searchQuery={searchQuery} />} />
          <Route path="/sports" element={<Sports searchQuery={searchQuery} />} />
          <Route path="/technology" element={<Technology searchQuery={searchQuery} />} />
          <Route path="/business" element={<Business searchQuery={searchQuery} />} />
          <Route path="/health" element={<Health searchQuery={searchQuery} />} />
          <Route path="/politics" element={<Politics searchQuery={searchQuery} />} />
          <Route path="/entertainment" element={<Entertainment searchQuery={searchQuery} />} />
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/contactus" element={<Contactus />} />
          <Route path="/privacypolicy" element={<PrivacyPolicy />} />
          {/* Catch-all route for 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {/* Scroll-to-top button */}
      <ScrollToTopButton />

      {/* Footer section */}
      <Footer />
    </div>
  );
};

export default App;
